import React, { useEffect, useRef, useState } from "react";
import "./clockInReminder.css";
import BdrModal from "../../components/BdrModal/BdrModal";
import BdrButton from "../../components/BdrButton/BdrButton";
import { useLazyQuery, useMutation } from "@apollo/client";
import Auth from "../auth";
import { hasRole, hasAnyRole } from "../permissions";
import { GET_OPEN_SHIFT } from "../GraphQL/queries";
import { CLOCK_IN_NOW } from "../GraphQL/mutations";
import { useSnackbar } from "../GlobalSnackbar/GlobalSnackbarContext";

// Per-tab marker set by the login handlers (Login.jsx / WebAuthnAuthenticate.jsx)
// on a successful login. A bare page refresh never runs those handlers, so the
// marker is absent on refresh — that's how we fire "every fresh login" without
// nagging on refresh (adminIsLoggedIn re-derives true on refresh and can't tell
// the two apart).
const FRESH_LOGIN_KEY = "bdr-clockin-fresh-login";
const CHECK_DELAY_MS = 60 * 1000;

/**
 * Office-only clock-in reminder. ~1 minute after an office user logs in, checks
 * whether they have an open shift; if not, pops a gradient modal (matching the
 * Create Calendar Event modal) offering to clock in via the same operation as
 * the navbar Clock In. Admins and developers never see it. Reminder only.
 */
export default function ClockInReminder() {
  const { handleSnack } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [clockBusy, setClockBusy] = useState(false);
  // Sticky: once we've shown (or intentionally suppressed) this login's
  // reminder, never re-open it on a later re-render.
  const handledRef = useRef(false);

  const [fetchOpenShift] = useLazyQuery(GET_OPEN_SHIFT, {
    fetchPolicy: "network-only",
  });
  const [clockInMutation] = useMutation(CLOCK_IN_NOW);

  useEffect(() => {
    // Office role ONLY — never admins or developers (per product decision, even
    // though the navbar lets developers clock in).
    if (!Auth.loggedIn()) return undefined;
    const perm = Auth.getPermissions();
    const isOfficeOnly =
      hasRole(perm, "office") && !hasAnyRole(perm, ["admin", "developer"]);
    if (!isOfficeOnly) return undefined;

    // Fire only after a real login, never on a bare refresh.
    if (sessionStorage.getItem(FRESH_LOGIN_KEY) !== "1") return undefined;

    let ignore = false;
    const timerId = setTimeout(async () => {
      if (ignore || handledRef.current) return;
      // Re-verify at fire time — the session may have ended during the 60s.
      // Guard loggedIn() first: getProfile() returns false (no .data) with no token.
      if (!Auth.loggedIn()) return;
      const userId = Auth.getProfile()?.data?._id;
      if (!userId) return;

      // Consume the marker: one check per login. A refresh AFTER this point
      // won't re-nag; a refresh BEFORE it re-arms the timer for the same login.
      sessionStorage.removeItem(FRESH_LOGIN_KEY);

      try {
        const { data } = await fetchOpenShift({ variables: { userId } });
        if (ignore || handledRef.current) return;
        const alreadyClockedIn = !!data?.getOpenShift;
        if (!alreadyClockedIn) {
          handledRef.current = true;
          setOpen(true);
        }
      } catch {
        // Network blip — stay silent; the navbar Clock In is always available.
      }
    }, CHECK_DELAY_MS);

    return () => {
      ignore = true;
      clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => setOpen(false);

  const handleClockIn = async () => {
    if (clockBusy) return;
    if (!Auth.loggedIn()) {
      setOpen(false);
      return;
    }
    const userId = Auth.getProfile()?.data?._id;
    if (!userId) {
      setOpen(false);
      return;
    }
    setClockBusy(true);
    try {
      const { data } = await clockInMutation({ variables: { userId } });
      if (data?.clockInNow) {
        handleSnack("Clocked in", 2000);
        // Same-tab sync: the navbar self-suppresses its own SSE origin, so tell
        // it to refetch the open shift and flip the Clock In/Out menu label.
        window.dispatchEvent(new CustomEvent("bdr:clockin-changed"));
        setOpen(false);
      }
    } catch (err) {
      handleSnack(err?.message || "Clock-in failed", 4000);
    } finally {
      setClockBusy(false);
    }
  };

  if (!open) return null;

  return (
    <BdrModal
      open={open}
      onClose={dismiss}
      title="You're not clocked in"
      variant="panel"
      size="sm"
      /* nag: fires on a timer, so it can arrive while a confirm is already
         open. Preserves the hand-picked z-index this carried before. */
      layer="nag"
      actions={
        <>
          <BdrButton onClick={dismiss} disabled={clockBusy}>
            Not now
          </BdrButton>
          {/* solid: variant="panel" is the saturated surface (§6b) */}
          <BdrButton
            variant="confirm-solid"
            onClick={handleClockIn}
            disabled={clockBusy}
          >
            {clockBusy ? "Clocking in..." : "Clock In"}
          </BdrButton>
        </>
      }
    >
      <p className="clockin-reminder-text">
        Would you like to clock in? If you're just checking something and don't
        need to be on the clock, you can dismiss this.
      </p>
    </BdrModal>
  );
}
